import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import { useSchedulesQuery } from '@hooks/useSchedules'
import reportPackService from '@api/v1/reportPackService'
import Modal from '@components/common/Modal'
import { fmtWhenInZone } from '@components/reports/queue/queueFormat'
import PackEditorModal from '@components/reports/packs/PackEditorModal'
import PackRunHistory from '@components/reports/packs/PackRunHistory'
import CombineRunsPanel from '@components/reports/packs/CombineRunsPanel'
import { PACK_FORMAT_LABELS, packableSchedules, runDeliveryText } from '@components/reports/packs/packFormat'
import { toast } from '@store/toastStore'

const QUEUE_PATH = '/reports/queue'
const PACKS_QUERY_KEY = ['report-packs']
const jobsEnabled = () => import.meta.env.VITE_REPORT_JOBS === 'true'

/**
 * Report packs (spec 172): several scheduled reports run together and sent as ONE email —
 * one Excel workbook with a sheet per report, or links to each — plus an on-demand "combine
 * finished runs into one workbook".
 *
 * A short owner-only list, so a semantic table rather than the shared DataGrid: each row
 * carries four actions and an expandable run history, and the list never paginates (a pack
 * holds 2–8 reports and an owner keeps a handful of packs).
 */
const ReportPacks = () => {
  useDocumentTitle('Report packs')
  const qc = useQueryClient()
  const enabled = jobsEnabled()
  const packsQuery = useQuery({ queryKey: PACKS_QUERY_KEY, queryFn: reportPackService.list, enabled, staleTime: 30_000 })
  const packsState = useReadState('report-packs', packsQuery)
  const packs = packsState === 'ready' ? packsQuery.data : []
  const schedulesQuery = useSchedulesQuery({ enabled, scope: 'mine' })
  const schedules = useMemo(() => packableSchedules(schedulesQuery.schedules), [schedulesQuery.schedules])

  const [editing, setEditing] = useState(null) // null | 'new' | pack
  const [openHistory, setOpenHistory] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const refresh = () => qc.invalidateQueries({ queryKey: PACKS_QUERY_KEY })
  const act = async (pack, fn, success) => {
    setBusyId(pack.id)
    try {
      await fn()
      toast.success(success)
      refresh()
      qc.invalidateQueries({ queryKey: ['report-pack-runs', pack.id] })
    } catch (e) {
      toast.error(e?.message || 'That did not work. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  if (!enabled) {
    return (
      <div className="report-shell">
        <h1 className="font-display text-2xl text-gray-900">Report packs</h1>
        <p className="mt-2 text-[13px] text-gray-700">Report packs need the report queue, which is not turned on here.</p>
      </div>
    )
  }

  return (
    <div className="report-shell">
      <div className="mb-4.5 flex flex-wrap items-start justify-between gap-3.5">
        <div>
          <div className="flex items-center gap-2">
            <Link to={QUEUE_PATH} className="btn-cb-toolbar" aria-label="Back to the report queue">←</Link>
            <h1 className="font-display text-2xl text-gray-900">Report packs</h1>
          </div>
          <p className="mt-1.5 text-[11.5px] text-gray-600">
            Several of your scheduled reports, run together and sent to you as one email.
          </p>
        </div>
        <button type="button" className="btn-cb-primary" onClick={() => setEditing('new')}>New report pack</button>
      </div>

      {packsState === 'loading' || packsState === 'idle' ? (
        <p className="text-[12.5px] text-gray-600">Loading your report packs…</p>
      ) : packsState !== 'ready' ? (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-800">
          Your report packs couldn&apos;t be loaded{packsQuery.error?.message ? `: ${packsQuery.error.message}` : ''}. This is not an empty list.{' '}
          <button type="button" onClick={() => packsQuery.refetch()} className="font-semibold text-cb-accent-text underline">Try again</button>
        </p>
      ) : packs.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-[12.5px] text-gray-700" data-testid="packs-empty">
          You have no report packs yet. A pack sends several of your scheduled reports together — for example, a Monday-morning set for a managing partner.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table role="table" className="w-full text-left text-[12.5px]">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
              <tr>
                <th scope="col" className="px-3 py-2">Pack</th>
                <th scope="col" className="px-3 py-2">Reports</th>
                <th scope="col" className="px-3 py-2">When</th>
                <th scope="col" className="px-3 py-2">Next run</th>
                <th scope="col" className="px-3 py-2">Last run</th>
                <th scope="col" className="px-3 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {packs.map((p) => {
                const last = p.runs?.[0] || null
                const status = !p.enabled ? 'Paused' : p.needsSignIn ? 'Needs sign-in' : null
                return [
                  <tr key={p.id} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{p.name}</div>
                      <div className="text-[11.5px] text-gray-600">{PACK_FORMAT_LABELS[p.deliverFormat] || p.deliverFormat}</div>
                      {status && <div className={`text-[11.5px] font-semibold ${p.needsSignIn ? 'text-red-800' : 'text-amber-800'}`}>{status}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      <ol className="list-decimal pl-4">{p.members.map((m) => <li key={m.position}>{m.label}</li>)}</ol>
                    </td>
                    <td className="px-3 py-2 text-gray-800">{p.cadence}</td>
                    <td className="px-3 py-2 text-gray-800" data-testid="pack-next-run">
                      {/* Never promise a run that will not happen: paused or dormant prints a dash. */}
                      {p.enabled && !p.needsSignIn && p.nextRunAt ? fmtWhenInZone(p.nextRunAt, p.timezone) : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-800">{last ? runDeliveryText(last) : 'Not run yet'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <button type="button" className="btn-cb-toolbar" disabled={busyId === p.id} aria-label={`Run now ${p.name}`}
                          onClick={() => act(p, () => reportPackService.runNow(p.id), `"${p.name}" is running. It will be emailed when every report finishes.`)}>
                          Run now
                        </button>
                        <button type="button" className="btn-cb-toolbar" aria-label={`Edit ${p.name}`} onClick={() => setEditing(p)}>
                          Edit
                        </button>
                        <button type="button" className="btn-cb-toolbar" disabled={busyId === p.id} aria-label={`${p.enabled ? 'Pause' : 'Resume'} ${p.name}`}
                          onClick={() => act(p, () => reportPackService.update(p.id, { enabled: !p.enabled }), p.enabled ? `"${p.name}" is paused.` : `"${p.name}" is active again.`)}>
                          {p.enabled ? 'Pause' : 'Resume'}
                        </button>
                        <button type="button" className="btn-cb-toolbar" aria-label={`Delete ${p.name}`} onClick={() => setDeleting(p)}>
                          Delete
                        </button>
                        <button type="button" className="btn-cb-toolbar" aria-expanded={openHistory === p.id} aria-label={`Run history for ${p.name}`}
                          onClick={() => setOpenHistory((cur) => (cur === p.id ? null : p.id))}>
                          Run history
                        </button>
                      </div>
                    </td>
                  </tr>,
                  openHistory === p.id && (
                    <tr key={`${p.id}-history`} className="border-t border-gray-100 bg-gray-50">
                      <td colSpan={6} className="px-3 py-3"><PackRunHistory pack={p} /></td>
                    </tr>
                  ),
                ]
              })}
            </tbody>
          </table>
        </div>
      )}

      <CombineRunsPanel />

      {editing && (
        <PackEditorModal
          pack={editing === 'new' ? null : editing}
          schedules={schedules}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null)
            toast.success(`Saved "${saved?.name || 'the pack'}".`)
            refresh()
          }}
        />
      )}
      {deleting && (
        // The shared Modal shell. (Chosen when ConfirmModal's overlay was aria-hidden; #353
        // fixed that, so either would do now. Kept as is: it works and is tested.)
        <Modal
          title="Delete report pack"
          onClose={() => setDeleting(null)}
          maxWidth="max-w-md"
          labelledBy="pack-delete-title"
          footer={(
            <div className="flex items-center justify-end gap-2">
              <button type="button" className="btn-cb-toolbar" onClick={() => setDeleting(null)}>Cancel</button>
              <button
                type="button"
                className="btn-cb-primary"
                onClick={async () => {
                  const p = deleting
                  setDeleting(null)
                  await act(p, () => reportPackService.remove(p.id), `Deleted "${p.name}".`)
                }}
              >
                Delete pack
              </button>
            </div>
          )}
        >
          <p className="text-[13px] text-gray-800">
            Delete &ldquo;{deleting.name}&rdquo;? Its run history and the saved results of its runs are deleted too.
            The schedules it was built from are not changed.
          </p>
        </Modal>
      )}
    </div>
  )
}

export default ReportPacks
